import { useState, useEffect, useRef } from 'react';
import { getCierres, createCierre, refreshCierre, generatePdfFromExcel, getResumenDiario, getResumenDiarioRango, getResumenDiarioDetallado, getCierreById, sendCierreEmails, searchCombinationsFromErrors as searchCombinationsFromErrorsAPI } from '@helpers/api/mantenedores/cierre';
import { getEmailTemplates } from '@helpers/api/mantenedores/emailTemplates';

/**
 * Hook para manejar el mantenedor de cierres de conductores
 * Maneja paginación, filtros y llamadas a la API
 * 
 * @param {Object} user - Usuario autenticado
 * @param {Object} permisos - Permisos del usuario
 * @param {Object} options - Opciones de configuración
 * @param {boolean} options.autoFetch - Si debe cargar cierres automáticamente (default: true)
 */

const INITIAL_PAGE_SIZE = 10;

export const useCierre = (user, permisos, options = {}) => {
  const { autoFetch = true } = options;
  
  const [cierres, setCierres] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaTermino, setFechaTermino] = useState('');
  
  // Estados para modal
  const [showModal, setShowModal] = useState(false);
  
  // Estado para loading específico del refresh de cierre
  const [refreshingCierreId, setRefreshingCierreId] = useState(null);
  
  // Estado para loading específico de la generación de PDF
  const [generatingPdfCierreId, setGeneratingPdfCierreId] = useState(null);
  
  // Control de cancelación de requests
  const abortControllerRef = useRef();
  const lastFetchId = useRef(0);
  
  // Control de auto-refresh para cierres en procesamiento
  const intervalRef = useRef(null);

  // Función para obtener cierres
  const fetchCierres = async (params = {}) => {
    // Cancelar request anterior
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const fetchId = ++lastFetchId.current;

    setLoading(true);
    setError(null);

    try {
      const requestParams = {
        pageSize,
        pageNumber,
        fecha_inicio: fechaInicio,
        fecha_termino: fechaTermino,
        ...params
      };
      
      const res = await getCierres(user, requestParams);
      
      // Solo actualizar si es la última request
      if (lastFetchId.current === fetchId && !controller.signal.aborted) {
        if (res.success) {
          setCierres(res.data);
          setTotal(res.total);
          
          // Verificar si hay cierres en procesamiento para activar auto-refresh
          const hasProcessing = res.data.some(cierre => 
            cierre.estado_actual?.estado === 'Procesando' || cierre.estado_actual?.estado === 'Generando PDF' || cierre.estado_actual?.estado === 'Enviando Correos'
          );
          
          if (hasProcessing && !intervalRef.current) {
            startAutoRefresh();
          } else if (!hasProcessing && intervalRef.current) {
            stopAutoRefresh();
          }
        } else {
          setCierres([]);
          setTotal(0);
          setError(res.error);
        }
      }
    } catch (err) {
      if (lastFetchId.current === fetchId && !controller.signal.aborted) {
        setCierres([]);
        setTotal(0);
        setError('Error al obtener cierres');
      }
    }
    
    if (lastFetchId.current === fetchId && !controller.signal.aborted) {
      setLoading(false);
    }
  };

  // Auto-refresh para cierres en procesamiento
  const startAutoRefresh = () => {
    if (intervalRef.current) return;
    
    intervalRef.current = setInterval(async () => {
      try {
        const params = {
          pageSize,
          pageNumber,
          fecha_inicio: fechaInicio,
          fecha_termino: fechaTermino
        };
        
        const res = await getCierres(user, params);
        if (res.success) {
          setCierres(res.data);
          setTotal(res.total);
          
          // Si no hay más cierres en procesamiento, detener el auto-refresh
          const hasProcessing = res.data.some(cierre => 
            cierre.estado_actual?.estado === 'Procesando' || cierre.estado_actual?.estado === 'Generando PDF' || cierre.estado_actual?.estado === 'Enviando Correos'
          );
          
          if (!hasProcessing) {
            stopAutoRefresh();
          }
        }
      } catch (error) {
        console.error('Error en auto-refresh:', error);
      }
    }, 120000); // 2 minutos
  };

  const stopAutoRefresh = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Effect para cargar datos iniciales y cuando cambien las dependencias
  // Solo se ejecuta si autoFetch está habilitado
  useEffect(() => {
    if (autoFetch) {
      fetchCierres();
    }
    
    // Cleanup: cancelar request y auto-refresh
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      stopAutoRefresh();
    };
  }, [user, pageSize, pageNumber, fechaInicio, fechaTermino, autoFetch]);

  // Cleanup al desmontar componente
  useEffect(() => {
    return () => {
      stopAutoRefresh();
    };
  }, []);

  // Handlers para filtros de fecha
  const handleFechaInicioChange = (fecha) => {
    setFechaInicio(fecha);
    setPageNumber(1);
  };

  const handleFechaTerminoChange = (fecha) => {
    setFechaTermino(fecha);
    setPageNumber(1);
  };

  // Handler para limpiar filtros
  const handleClearFilters = () => {
    setFechaInicio('');
    setFechaTermino('');
    setPageNumber(1);
  };

  // Paginación
  const handlePageChange = (page) => {
    setPageNumber(page);
  };

  // Cambio de tamaño de página
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPageNumber(1);
  };

  // Crear nuevo cierre
  const handleCreateCierre = async (cierreData) => {
    setLoading(true);
    try {
      const res = await createCierre(user, cierreData);
      if (res.success) {
        // Recargar la lista para mostrar el nuevo cierre
        await fetchCierres();
      }
      return res;
    } finally {
      setLoading(false);
    }
  };

  // Refresh de un cierre específico
  const handleRefreshCierre = async (cierreId) => {
    setRefreshingCierreId(cierreId);
    
    try {
      const res = await refreshCierre(user, cierreId);
      if (res.success) {
        // Recargar la lista para actualizar el estado
        await fetchCierres();
      }
      return res;
    } catch (error) {
      return {
        success: false,
        error: 'Error al actualizar cierre'
      };
    } finally {
      setRefreshingCierreId(null);
    }
  };

  // Generar PDF desde Excel para un cierre específico
  const handleGeneratePdf = async (cierreId) => {
    setGeneratingPdfCierreId(cierreId);
    
    try {
      const res = await generatePdfFromExcel(user, cierreId);
      if (res.success) {
        // Recargar la lista para actualizar el estado
        await fetchCierres();
      }
      return res;
    } catch (error) {
      return {
        success: false,
        error: 'Error al generar PDF'
      };
    } finally {
      setGeneratingPdfCierreId(null);
    }
  };

  // Refresh manual de la tabla
  const handleRefreshTable = () => {
    fetchCierres();
  };

  // Estados y funciones para resumen diario
  const [resumenDiario, setResumenDiario] = useState(null);
  const [loadingResumen, setLoadingResumen] = useState(false);
  const [errorResumen, setErrorResumen] = useState(null);

  // Estados para envío de correos
  const [cierreDetalle, setCierreDetalle] = useState(null);
  const [loadingCierreDetalle, setLoadingCierreDetalle] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);

  // Estados para templates de email
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loadingEmailTemplates, setLoadingEmailTemplates] = useState(false);

  // Obtener detalle de un cierre específico
  const loadCierreDetalle = async (cierreId) => {
    setLoadingCierreDetalle(true);
    setCierreDetalle(null);
    
    try {
      const res = await getCierreById(user, cierreId);
      
      if (res.success) {
        setCierreDetalle(res.data);
      } else {
        setCierreDetalle(null);
        setError(res.error);
      }
      
      return res;
    } catch (error) {
      setCierreDetalle(null);
      setError('Error al obtener detalle del cierre');
      return {
        success: false,
        error: 'Error al obtener detalle del cierre'
      };
    } finally {
      setLoadingCierreDetalle(false);
    }
  };

  // Cargar lista de templates de email disponibles
  const loadEmailTemplates = async () => {
    setLoadingEmailTemplates(true);
    try {
      const res = await getEmailTemplates(user);
      if (res.success) {
        setEmailTemplates(res.data);
      } else {
        setEmailTemplates([]);
      }
      return res;
    } catch (error) {
      setEmailTemplates([]);
      return { success: false, error: 'Error al obtener templates de email' };
    } finally {
      setLoadingEmailTemplates(false);
    }
  };

  // Enviar correos de un cierre
  const handleSendEmails = async (cierreId, emailData) => {
    setSendingEmails(true);
    
    try {
      const res = await sendCierreEmails(user, cierreId, emailData);
      
      if (res.success) {
        // Recargar la lista para actualizar el estado
        await fetchCierres();
      }
      
      return res;
    } catch (error) {
      return {
        success: false,
        error: 'Error al enviar correos del cierre'
      };
    } finally {
      setSendingEmails(false);
    }
  };

  // Obtener resumen diario por fecha específica
  const loadResumenDiario = async (fecha) => {
    setLoadingResumen(true);
    setErrorResumen(null);
    
    try {
      const fechaFormateada = typeof fecha === 'string' ? fecha : fecha.toISOString().split('T')[0];
      const res = await getResumenDiario(user, fechaFormateada);
      
      if (res.success) {
        setResumenDiario(res.data);
      } else {
        setResumenDiario(null);
        setErrorResumen(res.error);
      }
      
      return res;
    } catch (error) {
      setResumenDiario(null);
      setErrorResumen('Error al obtener resumen diario');
      return {
        success: false,
        error: 'Error al obtener resumen diario'
      };
    } finally {
      setLoadingResumen(false);
    }
  };

  // Obtener resumen diario por rango de fechas
  const loadResumenDiarioRango = async (fechaInicio, fechaFin) => {
    setLoadingResumen(true);
    setErrorResumen(null);
    
    try {
      const fechaInicioFormatted = typeof fechaInicio === 'string' ? fechaInicio : fechaInicio.toISOString().split('T')[0];
      const fechaFinFormatted = typeof fechaFin === 'string' ? fechaFin : fechaFin.toISOString().split('T')[0];
      
      const res = await getResumenDiarioRango(user, fechaInicioFormatted, fechaFinFormatted);
      
      if (res.success) {
        setResumenDiario(res.data);
      } else {
        setResumenDiario(null);
        setErrorResumen(res.error);
      }
      
      return res;
    } catch (error) {
      setResumenDiario(null);
      setErrorResumen('Error al obtener resumen diario');
      return {
        success: false,
        error: 'Error al obtener resumen diario'
      };
    } finally {
      setLoadingResumen(false);
    }
  };

  // Función para obtener resumen detallado de rango de fechas (máximo 40 días)
  const loadResumenDiarioDetallado = async (fechaInicio, fechaFin) => {
    if (!fechaInicio || !fechaFin) {
      console.log('Fechas no proporcionadas');
      return {
        success: false,
        error: 'Debe proporcionar fecha de inicio y fin'
      };
    }

    // Validar que el rango no exceda 40 días
    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);
    const diffTime = Math.abs(fin - inicio);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 40) {
      console.log('Rango de fechas excede 40 días');
      return {
        success: false,
        error: 'El rango de fechas no puede exceder los 40 días'
      };
    }

    setLoadingResumen(true);
    setErrorResumen(null);

    try {
      const response = await getResumenDiarioDetallado(user, fechaInicio, fechaFin);
      
      if (response && response.success) {
        setResumenDiario(response.data);
        return {
          success: true,
          data: response.data
        };
      } else {
        const errorMsg = response?.message || 'Error desconocido al obtener resumen detallado';
        console.error('Error en respuesta:', errorMsg);
        setErrorResumen(errorMsg);
        return {
          success: false,
          error: errorMsg
        };
      }
    } catch (error) {
      console.error('Error al obtener resumen detallado:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Error al conectar con el servidor';
      setErrorResumen(errorMsg);
      return {
        success: false,
        error: 'Error al obtener resumen detallado'
      };
    } finally {
      setLoadingResumen(false);
    }
  };

  // Buscar combinaciones de errores desde errores del cierre diario
  const searchCombinationsFromErrors = async (data) => {
    try {
      const res = await searchCombinationsFromErrorsAPI(user, data);
      return res;
    } catch (error) {
      console.error('Error al buscar combinaciones desde errores:', error);
      return {
        success: false,
        error: 'Error al buscar combinaciones desde errores'
      };
    }
  };

  return {
    // Estados principales
    cierres,
    loading,
    error,
    refreshingCierreId,
    generatingPdfCierreId,
    
    // Variables de paginación individuales (para compatibilidad)
    total,
    pageNumber,
    pageSize,
    
    // Paginación
    pagination: {
      currentPage: pageNumber,
      totalPages: Math.ceil(total / pageSize),
      totalItems: total,
      pageSize
    },
    
    // Filtros
    filters: {
      fechaInicio,
      fechaTermino
    },
    
    // Modal states
    showModal,
    setShowModal,
    
    // Acciones
    fetchCierres,
    handleCreateCierre,
    handleRefreshCierre,
    handleGeneratePdf,
    handleRefreshTable,
    setFilters: (newFilters) => {
      if (newFilters.fechaInicio !== undefined) setFechaInicio(newFilters.fechaInicio);
      if (newFilters.fechaTermino !== undefined) setFechaTermino(newFilters.fechaTermino);
      setPageNumber(1);
    },
    clearFilters: () => {
      setFechaInicio('');
      setFechaTermino('');
      setPageNumber(1);
    },
    
    // Funciones legacy para compatibilidad
    handleFechaInicioChange,
    handleFechaTerminoChange,
    handleClearFilters,
    handlePageChange,
    handlePageSizeChange,
    
    // Nuevas funciones para resumen diario
    resumenDiario,
    loadingResumen,
    errorResumen,
    loadResumenDiario,
    loadResumenDiarioRango,
    loadResumenDiarioDetallado,
    searchCombinationsFromErrors,
    
    // Nuevas funciones para envío de correos
    cierreDetalle,
    loadingCierreDetalle,
    sendingEmails,
    loadCierreDetalle,
    handleSendEmails,

    // Templates de email
    emailTemplates,
    loadingEmailTemplates,
    loadEmailTemplates
  };
};